import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  listSeasons,
  createSeason,
  updateSeason,
  deleteSeason,
} from "../../api/seasons";

const blank = () => ({
  name: "",
  typicalStartMonth: "",
  typicalEndMonth: "",
  displayOrder: 0,
  isActive: true,
});

export default function SeasonsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null); // null | {…} (form state)
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError("");
    listSeasons({ activeOnly: false })
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => setError(e.message || "Failed to load seasons"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => { setEditing(blank()); setEditingId(null); };
  const openEdit = (row) => {
    setEditing({
      name: row.name || "",
      typicalStartMonth: row.typicalStartMonth ?? "",
      typicalEndMonth: row.typicalEndMonth ?? "",
      displayOrder: row.displayOrder ?? 0,
      isActive: row.isActive !== false,
    });
    setEditingId(row.id);
  };
  const close = () => { setEditing(null); setEditingId(null); };

  const save = async () => {
    if (!editing.name.trim()) { setError("Name is required"); return; }
    const body = {
      name: editing.name.trim(),
      typicalStartMonth: editing.typicalStartMonth === "" ? null : Number(editing.typicalStartMonth),
      typicalEndMonth: editing.typicalEndMonth === "" ? null : Number(editing.typicalEndMonth),
      displayOrder: Number(editing.displayOrder) || 0,
      isActive: !!editing.isActive,
    };
    try {
      setSaving(true);
      if (editingId) await updateSeason(editingId, body);
      else await createSeason(body);
      close();
      fetchAll();
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete season "${row.name}"? If quotes, price rows or templates use it, it's made inactive instead.`)) return;
    try {
      setError("");
      // The API says whether it was deleted or, being in use, made inactive.
      const result = await deleteSeason(row.id);
      setNotice(result?.message || "");
      fetchAll();
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Seasons</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchAll}>Refresh</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>New Season</Button>
      </Stack>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice("")}>{notice}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Start Month</TableCell>
              <TableCell>End Month</TableCell>
              <TableCell>Display Order</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">No seasons</TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.typicalStartMonth ?? ""}</TableCell>
                <TableCell>{row.typicalEndMonth ?? ""}</TableCell>
                <TableCell>{row.displayOrder}</TableCell>
                <TableCell>{row.isActive ? "Yes" : "No"}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => remove(row)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editing !== null} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Edit Season" : "New Season"}</DialogTitle>
        <DialogContent>
          {editing && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Name" required fullWidth
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Typical Start Month" type="number"
                  inputProps={{ min: 1, max: 12 }}
                  value={editing.typicalStartMonth}
                  onChange={(e) => setEditing({ ...editing, typicalStartMonth: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Typical End Month" type="number"
                  inputProps={{ min: 1, max: 12 }}
                  value={editing.typicalEndMonth}
                  onChange={(e) => setEditing({ ...editing, typicalEndMonth: e.target.value })}
                  fullWidth
                />
              </Stack>
              <TextField
                label="Display Order" type="number"
                value={editing.displayOrder}
                onChange={(e) => setEditing({ ...editing, displayOrder: e.target.value })}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!editing.isActive}
                    onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
